import { useState, useRef, useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import ErrorBoundary from '@/components/shared/ErrorBoundary';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { uiActions, sessionActions, orgActions, sultanActions } from '@/store';
import {
  LayoutDashboard, Map, Car, Hexagon, MapPinned, Bell, Wrench, FileText, Play, Truck,
  Settings, ChevronLeft, ChevronRight, ChevronDown,
  Search, Globe, Check, LogOut, Shield, HelpCircle,
  Sun, Moon, Monitor, BarChart3, Crown,
  MessageCircle, Users, ClipboardCheck, ClipboardList, Route, Thermometer, Fuel, ShieldAlert, Brain, Sparkles,
  Building2, Plug, Repeat2, Palette, Bookmark, Clock, HardDrive, DatabaseBackup, DatabaseZap,
  Activity, Hourglass, Gauge, Package, LifeBuoy, Handshake, Megaphone, Siren, AlertOctagon,
  FileCheck, UserCog, Bus,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import api from '@/lib/api';
import ExpiryBanner from './ExpiryBanner';
import { auth } from '@/lib/trackizy';
import NotificationBell from '@/components/layout/NotificationBell';
import AppDownloadMenu from '@/components/layout/AppDownloadMenu';
import HelpAssistantWidget from '@/features/help-ai/HelpAssistantWidget';
import useBranding from '@/hooks/useBranding';
import useTheme from '@/hooks/useTheme';
import useFeatures from '@/hooks/useFeatures';
import useSupportConfig from '@/hooks/useSupportConfig';
import { useInstanceAccess } from '@/hooks/useInstanceAccess';
// setOrgToken removed — single JWT system now
import useAnalytics, { trackEvent } from '@/hooks/useAnalytics';
import useChatUnreadCount from '@/hooks/useChatUnreadCount';
import useSOS from '@/hooks/useSOS';
import { helpUrlForRoute } from '@/lib/helpRoutes';
import { resolveActiveSections, activeNavItem } from './navPolicy';

const LANGUAGES = [
  { code: 'en', label: 'English', flag: 'EN' },
  { code: 'ar', label: 'العربية', flag: 'ع' },
  { code: 'ckb', label: 'کوردی', flag: 'کو' },
];

export default function AppLayout() {
  const { t, i18n } = useTranslation();
  const branding = useBranding();
  const { theme, setTheme } = useTheme();
  const features = useFeatures();
  const supportConfig = useSupportConfig();

  // Start the analytics flush timer and keep org/user IDs in sync.
  useAnalytics();
  const chatUnreadCount = useChatUnreadCount(features.chat);
  const { activeCount: sosCount } = useSOS();

  const NAV_SECTIONS = [
    {
      key: 'liveOps',
      label: t('nav.section.liveOps', 'Live ops'),
      items: [
        { id: '/', icon: LayoutDashboard, label: t('nav.dashboard') },
        { id: '/map', icon: Map, label: t('nav.liveMap') },
        { id: '/walls', icon: Monitor, label: t('nav.walls', 'Walls'), feature: 'displayWall', adminOrManager: true },
        { id: '/vehicles', icon: Car, label: t('nav.vehicles') },
        { id: '/people', icon: Users, label: t('nav.people', 'People'), feature: 'workforce' },
        { id: '/alerts', icon: Bell, label: t('nav.alerts'), feature: 'alerts' },
        { id: '/chat', icon: MessageCircle, label: t('nav.chat', 'Chat'), feature: 'chat' },
        // Broadcast's server routes (GET/POST /api/broadcasts) are
        // admin/manager-only — adminOrManager mirrors that floor so a
        // viewer isn't offered a nav entry that will 403.
        { id: '/broadcast', icon: Megaphone, label: t('nav.broadcast', 'Broadcast'), feature: 'broadcast', adminOrManager: true },
        { id: '/safety', icon: ShieldAlert, label: t('nav.fieldSafety', 'Field Safety'), feature: 'guardian' },
        // Muster's board (declare/mark/close/export) is admin/manager only
        // server-side, same as Broadcast — adminOrManager mirrors that
        // floor so a viewer isn't offered a nav entry that will 403.
        { id: '/muster', icon: Siren, label: t('nav.muster', 'Muster'), feature: 'muster', adminOrManager: true },
        // Every /api/incidents route except the worker-facing /mine
        // (unused by this nav entry's target page) is admin/manager-only
        // server-side — adminOrManager mirrors that floor, same as
        // Broadcast and Muster above.
        { id: '/incidents', icon: AlertOctagon, label: t('nav.incidents', 'Incidents'), feature: 'respond', adminOrManager: true },
        { id: '/assure', icon: FileCheck, label: t('nav.assure', 'Duty of care'), feature: 'assure', adminOrManager: true },
        { id: '/work', icon: ClipboardList, label: t('nav.work', 'Work'), feature: 'work' },
        // Staff attendance was here — now embedded in /people as the
        // "Attendance" tab. The /attendance route still exists for direct
        // links but the sidebar entry was dropped.
        { id: '/crm', icon: Users, label: t('nav.crm', 'CRM'), feature: 'crm' },
        { id: '/cold-chain', icon: Thermometer, label: t('nav.coldChain', 'Cold Chain'), feature: 'temperature' },
        { id: '/fuel', icon: Fuel, label: t('nav.fuel', 'Fuel'), feature: 'fuel' },
        // /api/school-bus is admin/manager only server-side.
        { id: '/school-bus', icon: Bus, label: t('nav.schoolBus', 'School Bus'), feature: ['schoolBus', 'schoolBusStandard'], adminOrManager: true }, // Standard is a tier of School Bus: both must be on
      ],
    },
    {
      key: 'analysis',
      label: t('nav.section.analysis', 'Analysis'),
      items: [
        { id: '/reports', icon: FileText, label: t('nav.reports'), feature: 'reports' },
        { id: '/replay', icon: Play, label: t('nav.replay'), feature: 'replay' },
        { id: '/drivers', icon: Shield, label: t('nav.driverScorecard', 'Driver Scorecard'), feature: 'driverSafety' },
        { id: '/intelligence', icon: Brain, label: t('nav.intelligence', 'Intelligence'), feature: 'ai' },
      ],
    },
    {
      key: 'setup',
      label: t('nav.section.setup', 'Setup'),
      items: [
        { id: '/geofences', icon: Hexagon, label: t('nav.geofences'), feature: 'geofences' },
        { id: '/pois', icon: MapPinned, label: t('nav.pois'), feature: 'pois' },
        { id: '/maintenance', icon: Wrench, label: t('nav.maintenance'), feature: 'maintenance' },
        { id: '/forms', icon: ClipboardCheck, label: t('nav.forms', 'Forms'), feature: 'forms' },
        { id: '/work-templates', icon: Bookmark, label: t('nav.workTemplates', 'Work templates'), feature: 'workTemplates' },
      ],
    },
    {
      key: 'admin',
      label: t('nav.section.admin', 'Admin'),
      items: [
        { id: '/settings', icon: Settings, label: t('nav.settings') },
        // Gated on the instance's support-bridge config, not a featureX flag.
        ...(supportConfig.enabled
          ? [{ id: '/support', icon: LifeBuoy, label: t('nav.support', 'Support') }]
          : []),
      ],
    },
  ];

  const PLATFORM_SECTIONS = [
    {
      key: 'platform',
      label: t('nav.section.platform', 'Platform'),
      items: [
        { id: '/sultan', icon: Building2, label: t('nav.orgs', 'Orgs') },
        { id: '/sultan/resellers', icon: Handshake, label: t('nav.resellers', 'Resellers'), cap: 'resellers' },
        { id: '/sultan/expiry', icon: Hourglass, label: t('nav.expiry', 'Expiry') },
        { id: '/sultan/imports', icon: DatabaseZap, label: t('nav.imports', 'Imports') },
        { id: '/sultan/connectivity', icon: Activity, label: t('nav.connectivity', 'Connectivity') },
        { id: '/sultan/sensor-health', icon: Gauge, label: t('nav.sensorHealth', 'Sensor Health') },
        { id: '/analytics', icon: BarChart3, label: t('nav.analytics') },
        { id: '/sultan/integrations', icon: Plug, label: t('nav.integrations', 'Integrations'), cap: 'admin' },
        { id: '/sultan/ai', icon: Sparkles, label: t('nav.ai', 'AI'), cap: 'admin' },
        { id: '/sultan/branding', icon: Palette, label: t('nav.branding', 'Branding'), cap: 'admin' },
        { id: '/sultan/modules', icon: Package, label: t('nav.modulesCatalog', 'Modules'), cap: 'modules' },
        { id: '/sultan/team', icon: UserCog, label: t('nav.team', 'Team'), cap: 'admin' },
        { id: '/sultan/servers', icon: HardDrive, label: t('nav.servers', 'Servers'), cap: 'admin' },
        { id: '/sultan/backups', icon: DatabaseBackup, label: t('nav.backups', 'Backups'), cap: 'admin' },
      ],
    },
  ];

  // Reseller-admin nav section (spec 2026-07-11): shown only when the
  // session carries a reseller context AND there's no active org — a
  // reseller admin who's impersonating a customer org (org context
  // present) sees the normal fleet nav; the ImpersonationBanner is their
  // way back to /reseller.
  const RESELLER_SECTIONS = [
    {
      key: 'reseller',
      label: t('nav.section.reseller', 'Reseller'),
      items: [
        { id: '/reseller', icon: Building2, label: t('nav.resellerOverview', 'My Organizations') },
      ],
    },
  ];

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const collapsed = useSelector((state) => state.ui.sidebarCollapsed);
  const sidebarMode = useSelector((state) => state.ui.sidebarMode);

  const devices = useSelector((state) => state.devices.items);
  const user = useSelector((state) => state.session.user);
  const access = useInstanceAccess();
  const isHub = useSelector((state) => state.session.isHub);
  const orgName = useSelector((state) => state.org?.name);
  const orgId = useSelector((state) => state.org?.id);
  const resellerCtx = useSelector((state) => state.session.reseller);
  // Which sections this session sees is pure policy — see navPolicy.js.
  // On the hub deployment an Instance Admin gets only the fleet-registry
  // items; on instances, Servers + Backups are hidden (the API also 403s
  // off-hub); a support user sees the baseline platform items plus whatever
  // its capabilities unlock.
  const activeSections = resolveActiveSections({
    isHub,
    access,
    sidebarMode,
    hasReseller: Boolean(resellerCtx),
    hasOrg: Boolean(orgId),
    navSections: NAV_SECTIONS,
    platformSections: PLATFORM_SECTIONS,
    resellerSections: RESELLER_SECTIONS,
  });
  const orgRole = useSelector((state) => state.org?.role);
  const impersonating = useSelector((state) => !!state.sultan?.impersonatingOrgId);
  // Map-immersive pages render their own floating controls and need to
  // suppress the AppLayout 48px top bar so it doesn't push map UI down.
  // See src/CLAUDE.md "Map-immersive pages follow the WorkBoard / Live Map pattern".
  const isMapView = pathname === '/map' || pathname === '/work';
  const isFullHeightView = isMapView || pathname.startsWith('/reports');

  const deviceCount = Object.keys(devices).length;
  const onlineCount = Object.values(devices).filter((d) => d.status === 'online').length;

  const activeItem = activeNavItem([...NAV_SECTIONS, ...PLATFORM_SECTIONS, ...RESELLER_SECTIONS].flatMap((s) => s.items), pathname);
  const pageTitle = activeItem?.label || 'Dashboard';

  // Track page views on every route change.
  useEffect(() => {
    trackEvent('page_view', { page: pathname });
  }, [pathname]);

  // Track login: fire once when user transitions from null/undefined to a valid user object.
  // Done here (not LoginPage) so org_id is guaranteed available via the orgSlice.
  const prevUserRef = useRef(null);
  useEffect(() => {
    if (!prevUserRef.current && user) {
      trackEvent('login', {});
    }
    prevUserRef.current = user;
  }, [user]);

  const [langOpen, setLangOpen] = useState(false);
  const langRef = useRef(null);

  useEffect(() => {
    if (!langOpen) return;
    const handleClick = (e) => {
      if (langRef.current && !langRef.current.contains(e.target)) setLangOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [langOpen]);

  return (
    <div className={`h-screen flex overflow-hidden${impersonating ? ' pt-9' : ''}`}>
      {/* ═══ Sidebar ═══ */}
      <aside
        className={cn(
          'flex flex-col transition-all duration-300 flex-shrink-0 relative z-20',
          collapsed ? 'w-[64px]' : 'w-[220px]',
        )}
        style={{
          background: 'rgba(15, 23, 42, 0.92)',
          backdropFilter: 'blur(40px) saturate(180%)',
          WebkitBackdropFilter: 'blur(40px) saturate(180%)',
          borderInlineEnd: '1px solid rgba(255, 255, 255, 0.06)',
        }}
      >
        {/* Logo */}
        <div className={cn('h-14 flex items-center border-b border-white/[0.06]', collapsed ? 'justify-center' : 'px-4')}>
          {!collapsed ? (
            <div className="flex items-center gap-2.5">
              <img src={branding.logoUrl || '/assets/logo.png'} alt={branding.name} className="w-8 h-8 rounded-xl object-contain"
                onError={(e) => { e.target.src = '/assets/logo.png'; }} />
              <div>
                <span className="text-sm font-bold text-white tracking-tight">{branding.name}</span>
                <p className="text-[9px] text-white/30 -mt-0.5">{branding.tagline}</p>
              </div>
            </div>
          ) : (
            <img src={branding.logoUrl || '/assets/logo.png'} alt={branding.name} className="w-9 h-9 rounded-xl object-contain"
              onError={(e) => { e.target.src = '/assets/logo.png'; }} />
          )}
        </div>

        {/* Search */}
        {!collapsed && (
          <div className="px-3 py-2.5">
            <button
              onClick={() => dispatch(uiActions.setCommandPalette(true))}
              className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-white/40 text-xs hover:bg-white/[0.08] transition-all"
              style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.06)' }}
            >
              <Search size={13} />
              <span className="flex-1 text-start">{t('sidebar.search')}</span>
              <kbd className="text-[9px] px-1.5 py-0.5 rounded-md" style={{ background: 'rgba(255,255,255,0.08)' }}>⌘K</kbd>
            </button>
          </div>
        )}

        {/* Mode switcher (instance staff only) */}
        {access.isStaff && (
          !collapsed ? (
            <div className="px-3 pb-2">
              <button
                onClick={() => {
                  const next = sidebarMode === 'platform' ? 'fleet' : 'platform';
                  dispatch(uiActions.setSidebarMode(next));
                  navigate(next === 'platform' ? '/sultan' : '/');
                }}
                className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold text-white/70 hover:text-white hover:bg-white/[0.08] transition-all"
                style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.06)' }}
                title={t('nav.flipMode', 'Switch mode')}
              >
                {sidebarMode === 'platform'
                  ? <Crown size={14} className="text-amber-400 flex-shrink-0" />
                  : <Truck size={14} className="text-sky-400 flex-shrink-0" />}
                <span className="flex-1 text-start truncate">
                  {sidebarMode === 'platform'
                    ? t('nav.modePlatform', 'Platform Admin')
                    : t('nav.modeFleet', 'Fleet: {{org}}', { org: orgName || '—' })}
                </span>
                <Repeat2 size={12} className="text-white/30 flex-shrink-0" />
              </button>
            </div>
          ) : (
            <div className="px-2 pb-2 flex justify-center">
              <button
                onClick={() => {
                  const next = sidebarMode === 'platform' ? 'fleet' : 'platform';
                  dispatch(uiActions.setSidebarMode(next));
                  navigate(next === 'platform' ? '/sultan' : '/');
                }}
                className="w-9 h-9 flex items-center justify-center rounded-xl text-white/60 hover:text-white hover:bg-white/[0.08] transition-all"
                style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.06)' }}
                title={sidebarMode === 'platform' ? t('nav.modePlatform', 'Platform Admin') : t('nav.modeFleet', 'Fleet')}
              >
                {sidebarMode === 'platform'
                  ? <Crown size={14} className="text-amber-400" />
                  : <Truck size={14} className="text-sky-400" />}
              </button>
            </div>
          )
        )}

        {/* Nav */}
        <nav className="flex-1 py-1 px-2 overflow-y-auto">
          {activeSections.map((section, sectionIdx) => {
            const navIsAdmin = user?.administrator || orgRole === 'admin' || access.isAdmin;
            const visibleItems = section.items.filter((item) => (
              (!item.feature || [].concat(item.feature).every((k) => features[k])) &&
              (!item.admin || navIsAdmin) &&
              (!item.adminOrManager || navIsAdmin || orgRole === 'manager') &&
              (!item.sultan || access.isAdmin)
            ));
            if (visibleItems.length === 0) return null;
            return (
              <div key={section.key} className={sectionIdx === 0 ? '' : 'mt-3'}>
                {!collapsed ? (
                  <div className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-white/30 select-none">
                    {section.label}
                  </div>
                ) : (
                  sectionIdx > 0 && <div className="mx-2 my-2 border-t border-white/[0.06]" />
                )}
                {visibleItems.map((item) => {
                  const isActive = item.id === activeItem?.id;
                  return (
                    <button
                      key={item.id}
                      onClick={() => navigate(item.id)}
                      title={collapsed ? item.label : undefined}
                      className={cn(
                        'w-full flex items-center gap-2.5 rounded-xl text-start transition-all duration-150 group relative mb-0.5',
                        collapsed ? 'justify-center px-2 py-2.5' : 'px-3 py-2.5',
                        isActive ? 'text-white' : 'text-white/40 hover:text-white/70 hover:bg-white/[0.06]',
                      )}
                      style={isActive ? { background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.08)' } : {}}
                    >
                      {isActive && <div className="absolute top-1/2 -translate-y-1/2 w-[3px] h-5 bg-brand rounded-r-full shadow-sm shadow-brand/50" style={{ insetInlineStart: 0 }} />}
                      <item.icon size={18} className={isActive ? 'text-brand' : 'text-white/30 group-hover:text-white/50'} />
                      {!collapsed && (
                        <>
                          <span className="text-[13px] font-medium flex-1">{item.label}</span>
                          {item.id === '/chat' && chatUnreadCount > 0 && (
                            <span className="ms-auto w-5 h-5 rounded-full bg-sky-500 text-white text-[10px] flex items-center justify-center">
                              {chatUnreadCount > 99 ? '99+' : chatUnreadCount}
                            </span>
                          )}
                          {item.id === '/safety' && sosCount > 0 && (
                            <span className="ms-auto w-5 h-5 rounded-full bg-red-500 text-white text-[10px] flex items-center justify-center animate-pulse">
                              {sosCount}
                            </span>
                          )}
                          {item.children && <ChevronDown size={13} className="text-white/20" />}
                        </>
                      )}
                    </button>
                  );
                })}
              </div>
            );
          })}
        </nav>

        {/* Collapse + User */}
        <div className="border-t border-white/[0.06] p-3 space-y-2">
          <div className={cn('flex', collapsed ? 'justify-center' : 'justify-end')}>
            <button
              onClick={() => dispatch(uiActions.toggleSidebar())}
              className="p-1.5 rounded-lg text-white/30 hover:text-white/60 hover:bg-white/[0.06] transition-all"
            >
              {collapsed ? <ChevronRight size={15} /> : <ChevronLeft size={15} />}
            </button>
          </div>
          {!collapsed ? (
            <div className="flex items-center justify-between">
              <button onClick={() => navigate('/settings/account')} className="flex items-center gap-2 hover:opacity-80 transition-opacity">
                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-brand to-brand/80 flex items-center justify-center text-white text-[10px] font-bold shadow-sm shadow-brand/30">
                  {(user?.name || '?')[0].toUpperCase()}
                </div>
                <div>
                  <p className="text-[11px] font-medium text-white/60">{user?.name || '—'}</p>
                  <p className="text-[9px] text-white/25">{orgName || (user?.administrator ? t('settings.admin') : t('settings.user'))}</p>
                </div>
              </button>
              <button
                onClick={async () => { await auth.logout(); dispatch(orgActions.clearOrg()); dispatch(sultanActions.clearSultan()); dispatch(sessionActions.clearUser()); navigate('/login', { replace: true }); }}
                className="p-1.5 rounded-lg text-white/20 hover:text-red-400 hover:bg-white/[0.06] transition-all"
                title={t('nav.logout')}
              >
                <LogOut size={14} />
              </button>
            </div>
          ) : (
            <div className="flex justify-center">
              <button
                onClick={() => navigate('/settings/account')}
                className="w-7 h-7 rounded-full bg-gradient-to-br from-brand to-brand/80 flex items-center justify-center text-white text-[10px] font-bold shadow-sm shadow-brand/30 hover:opacity-80 transition-opacity"
              >
                {(user?.name || '?')[0].toUpperCase()}
              </button>
            </div>
          )}
        </div>
      </aside>

      {/* ═══ Main Content ═══ */}
      <main className={cn('flex-1 flex flex-col overflow-hidden', isMapView ? '' : 'bg-slate-50 dark:bg-slate-950')}>
        <ExpiryBanner />
        {/* Top bar — glass on map, solid otherwise */}
        {!isMapView && (
          <header
            className="h-12 flex items-center justify-between px-4 flex-shrink-0 z-10 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800"
          >
            <div className="flex items-center gap-2.5">
              <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-200">{pageTitle}</h2>
              <span className="text-[10px] text-slate-500 dark:text-slate-400 px-2 py-0.5 rounded-full tabular-nums bg-slate-100 dark:bg-slate-800">
                {onlineCount} {t('topbar.online')} · {deviceCount} {t('topbar.total')}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <AppDownloadMenu />
              <NotificationBell variant="solid" />
              <button
                onClick={() => navigate(helpUrlForRoute(pathname))}
                className={cn(
                  'p-2 rounded-xl transition-all hover:bg-slate-100 dark:hover:bg-slate-800',
                  pathname === '/help' && 'bg-slate-100 dark:bg-slate-800'
                )}
                title={t('help.openForThisScreen', 'Help for this screen')}
                aria-label={t('help.openForThisScreen', 'Help for this screen')}
              >
                <HelpCircle size={16} className="text-slate-500 dark:text-slate-400" />
              </button>
              {(() => {
                const ThemeIcon = theme === 'system' ? Monitor : theme === 'dark' ? Moon : Sun;
                return (
                  <button
                    onClick={() => {
                      const next = theme === 'system' ? 'light' : theme === 'light' ? 'dark' : 'system';
                      setTheme(next);
                    }}
                    className="p-2 rounded-xl transition-all hover:bg-slate-100 dark:hover:bg-slate-800"
                    title={t('theme.theme')}
                  >
                    <ThemeIcon size={16} className="text-slate-500 dark:text-slate-400" />
                  </button>
                );
              })()}
              <div className="relative" ref={langRef}>
                <button
                  onClick={() => setLangOpen(!langOpen)}
                  className={cn('p-2 rounded-xl transition-all hover:bg-slate-100 dark:hover:bg-slate-800', langOpen && 'bg-slate-100 dark:bg-slate-800')}
                >
                  <Globe size={16} className="text-slate-500 dark:text-slate-400" />
                </button>
                {langOpen && (
                  <div
                    className="absolute end-0 top-full mt-1.5 w-40 rounded-xl overflow-hidden shadow-lg z-50 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700"
                  >
                    {LANGUAGES.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => { i18n.changeLanguage(lang.code); setLangOpen(false); }}
                        className="w-full flex items-center gap-2.5 px-3 py-2 text-xs hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                      >
                        <span className="w-5 h-5 rounded-md bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-[10px] font-bold text-slate-600 dark:text-slate-400">
                          {lang.flag}
                        </span>
                        <span className="flex-1 text-start text-slate-700 dark:text-slate-300 font-medium">{lang.label}</span>
                        {i18n.language === lang.code && <Check size={14} className="text-blue-500" />}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </header>
        )}

        {/* Page content — map view gets full area with no header */}
        <div className={cn('flex-1', isFullHeightView ? 'overflow-hidden' : 'overflow-auto')}>
          <ErrorBoundary>
            <Outlet context={{ onlineCount, deviceCount }} />
          </ErrorBoundary>
        </div>
      </main>
      <HelpAssistantWidget />
    </div>
  );
}
